import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Timer, ChevronLeft, ChevronRight, Flag, CheckCircle2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, V1 } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ExamRunner() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [remaining, setRemaining] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [blurCount, setBlurCount] = useState(0);
  const [warning, setWarning] = useState("");
  const timerRef = useRef(null);
  const submittedRef = useRef(false);

  useEffect(() => {
    const cached = sessionStorage.getItem(`sano_session_${sessionId}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      setData(parsed);
      if (parsed.duration_seconds) {
        const started = new Date(parsed.started_at).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - started) / 1000);
        setRemaining(Math.max(parsed.duration_seconds - elapsed, 0));
      }
    } else {
      toast.error("Sınav oturumu bulunamadı");
      navigate("/deneme-sinavlari");
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    if (remaining === null) return;
    if (remaining <= 0) { submit(); return; }
    timerRef.current = setInterval(() => setRemaining((r) => (r !== null ? r - 1 : null)), 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  // Security: report tab blur / visibility change / copy attempts
  useEffect(() => {
    if (!sessionId) return;
    const report = (kind, meta) => {
      api.post(`${V1}/exam-sessions/${sessionId}/security-events`, { kind, meta }).catch(() => {});
    };
    const onVisibility = () => {
      if (document.hidden) {
        report("visibility_hidden");
        setBlurCount((c) => c + 1);
        setWarning("Uyarı: Sınav sekmesinden ayrıldın. Bu olay kaydedildi.");
      } else {
        report("visibility_visible");
      }
    };
    const onBlur = () => {
      report("blur");
      setBlurCount((c) => c + 1);
      setWarning("Uyarı: Sınav penceresi fokusu kaybetti. Olay kaydedildi.");
    };
    const onCopy = () => { report("copy"); };
    const onContext = (e) => { e.preventDefault(); report("context_menu"); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    window.addEventListener("copy", onCopy);
    window.addEventListener("contextmenu", onContext);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("copy", onCopy);
      window.removeEventListener("contextmenu", onContext);
    };
  }, [sessionId]);

  const saveAnswer = async (qid, opt) => {
    setAnswers((a) => ({ ...a, [qid]: opt }));
    try {
      await api.post(`${V1}/exam-sessions/answer?session_id=${sessionId}`, { question_id: qid, option_key: opt });
    } catch (err) { /* ignore */ }
  };

  const submit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    try {
      await api.post(`${V1}/exam-sessions/submit`, { session_id: sessionId });
      sessionStorage.removeItem(`sano_session_${sessionId}`);
      navigate(`/sinav-sonuc/${sessionId}`);
    } catch (e) {
      submittedRef.current = false;
      toast.error(e?.response?.data?.detail || "Sınav teslim edilemedi");
    }
  };

  if (!data) return <div className="p-16 text-center text-muted-foreground">Sınav yükleniyor…</div>;
  const questions = data.questions || [];
  const q = questions[current];
  const answered = Object.keys(answers).length;
  const fmt = (s) => {
    if (s === null || s === undefined) return "—";
    const m = Math.floor(s / 60), sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col select-none" data-testid="exam-runner" onCopy={(e) => e.preventDefault()}>
      {/* Sticky header */}
      <div className="sticky top-0 z-30 border-b border-border glass">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">Oturum #{sessionId.slice(0, 8)}</div>
            <div className="font-display font-bold">Deneme Sınavı</div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono font-bold text-lg ${remaining !== null && remaining < 300 ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`} data-testid="exam-timer">
            <Timer className="h-5 w-5" /> {fmt(remaining)}
          </div>
          <Button variant="destructive" onClick={() => setConfirmOpen(true)} data-testid="exam-submit-btn">
            <CheckCircle2 className="h-4 w-4" /> Sınavı Tamamla
          </Button>
        </div>
        <div className="h-1 bg-muted">
          <div className="h-full bg-accent transition-all" style={{ width: `${(answered / questions.length) * 100}%` }} />
        </div>
        {warning && (
          <div className="bg-destructive/10 text-destructive text-xs px-4 py-2 flex items-center gap-2" data-testid="exam-security-warning">
            <ShieldAlert className="h-4 w-4" /> {warning} <span className="ml-auto opacity-70">Uyarı sayısı: {blurCount}</span>
          </div>
        )}
      </div>

      <div className="flex-1 grid lg:grid-cols-[1fr_260px] gap-6 mx-auto max-w-6xl w-full px-4 py-8">
        <div>
          {q && (
            <div className="rounded-xl border border-border bg-card p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="overline text-accent">Soru {current + 1} / {questions.length}</div>
                  {q.subject && <div className="text-xs text-muted-foreground mt-1">{q.subject}{q.topic ? ` · ${q.topic}` : ""}</div>}
                </div>
                <button
                  onClick={() => setFlagged((f) => ({ ...f, [q.id]: !f[q.id] }))}
                  className={`inline-flex items-center gap-1 text-sm ${flagged[q.id] ? "text-accent" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`flag-btn-${q.id}`}
                >
                  <Flag className="h-4 w-4" /> {flagged[q.id] ? "İşaretlendi" : "İşaretle"}
                </button>
              </div>
              {q.image_url && <img src={q.image_url} alt="soru" className="max-w-full rounded mb-4 border border-border" />}
              <div className="text-lg leading-relaxed mb-6 whitespace-pre-line">{q.text}</div>
              <div className="grid gap-3">
                {q.options.map((o) => {
                  const selected = answers[q.id] === o.key;
                  return (
                    <button
                      key={o.key}
                      onClick={() => saveAnswer(q.id, o.key)}
                      data-testid={`option-${o.key}`}
                      className={`text-left p-4 rounded-lg border transition-colors flex items-start gap-3 ${
                        selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 bg-background"
                      }`}
                      style={{ transition: "border-color 0.15s ease, background-color 0.15s ease" }}
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-display font-bold text-sm ${selected ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{o.key}</span>
                      <span className="pt-1">{o.text}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-8 flex justify-between">
                <Button variant="outline" onClick={() => setCurrent((c) => Math.max(0, c - 1))} disabled={current === 0} data-testid="prev-btn">
                  <ChevronLeft className="h-4 w-4" /> Önceki
                </Button>
                <Button onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))} disabled={current === questions.length - 1} data-testid="next-btn">
                  Sonraki <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start rounded-xl border border-border bg-card p-4">
          <div className="text-sm mb-3 font-medium">Soru Haritası</div>
          <div className="grid grid-cols-6 lg:grid-cols-5 gap-2">
            {questions.map((qq, i) => {
              const ans = !!answers[qq.id];
              const isFlag = flagged[qq.id];
              return (
                <button
                  key={qq.id}
                  onClick={() => setCurrent(i)}
                  data-testid={`nav-q-${i + 1}`}
                  className={`aspect-square rounded text-xs font-mono font-semibold border relative ${
                    current === i ? "border-primary ring-2 ring-primary" :
                    ans ? "border-primary/50 bg-primary/10" : "border-border bg-background"
                  }`}
                >
                  {i + 1}
                  {isFlag && <Flag className="h-2.5 w-2.5 absolute top-0.5 right-0.5 text-accent" />}
                </button>
              );
            })}
          </div>
          <div className="mt-4 text-xs text-muted-foreground space-y-1">
            <div>Cevaplanan: <b className="text-foreground">{answered}/{questions.length}</b></div>
            <div>İşaretlenen: <b className="text-foreground">{Object.values(flagged).filter(Boolean).length}</b></div>
            <div>Uyarı: <b className="text-destructive">{blurCount}</b></div>
          </div>
        </aside>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sınavı tamamlamak istediğinize emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              <b className="text-foreground">{answered}</b> soruyu cevapladınız. Onayladığınızda cevaplarınız kilitlenir ve düzenlenemez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={submit} data-testid="confirm-submit-btn">Evet, teslim et</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
